import React, { useEffect, useState } from 'react';

import {
  createCustomPlaylist,
  getSelectableAudioReferences,
  updateCustomPlaylist,
} from '../../services/customAudioPlaylistService';
import { containsProfanity } from '../../utils/containsProfanity';
import ChapterSelector from '../Common/ChapterSelector/ChapterSelector';
import ModalBase from '../ModalBase/ModalBase';

const MAX_NAME_LENGTH = 200;

function CreatePlaylistModal({
  isOpen,
  onClose,
  onSuccess,
  editingPlaylist,
  existingPlaylists = [],
  pfeSktId,
}) {
  const [name, setName] = useState('');
  const [references, setReferences] = useState([]);
  const [selectedChapterIds, setSelectedChapterIds] = useState(new Set());

  const [loadingChapters, setLoadingChapters] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const isEditMode = !!editingPlaylist;

  // Fetch available chapters when modal opens
  useEffect(() => {
    if (!isOpen) return;

    // Reset state on open
    setReferences([]);
    setError('');
    setSaving(false);

    if (isEditMode) {
      setName(editingPlaylist.name || '');
      setSelectedChapterIds(
        new Set(editingPlaylist.chapters.map((ch) => ch.chapterId)),
      );
    } else {
      setName('');
      setSelectedChapterIds(new Set());
    }

    const fetchChapters = async () => {
      if (!pfeSktId) {
        setError('Could not determine the active rate.');
        return;
      }

      setLoadingChapters(true);

      try {
        const data = await getSelectableAudioReferences(pfeSktId);
        setReferences(data);
      } catch (err) {
        setError('Could not load available chapters. Please try again.');
      } finally {
        setLoadingChapters(false);
      }
    };

    fetchChapters();
  }, [isOpen, pfeSktId]);

  // Filter out legacy chapters once references are loaded
  useEffect(() => {
    if (!isEditMode || !references.length || !selectedChapterIds.size) return;

    const selectableIds = new Set(
      references.flatMap((ref) => (ref.chapters || []).map((ch) => ch.id)),
    );

    const filtered = new Set(
      [...selectedChapterIds].filter((id) => selectableIds.has(id)),
    );

    if (filtered.size !== selectedChapterIds.size) {
      setSelectedChapterIds(filtered);
    }
  }, [references]);

  const handleNameChange = (e) => {
    const value = e.target.value;
    if (value.length <= MAX_NAME_LENGTH) {
      setName(value);
      if (error) setError('');
    }
  };

  const normalizePlaylistName = (value) => (value || '').trim().toLowerCase();

  const hasChanges = () => {
    if (!isEditMode) return true;

    const nameChanged =
      normalizePlaylistName(name) !==
      normalizePlaylistName(editingPlaylist.name);

    const originalChapterIds = new Set(
      editingPlaylist.chapters.map((ch) => ch.chapterId),
    );
    const chaptersChanged =
      selectedChapterIds.size !== originalChapterIds.size ||
      [...selectedChapterIds].some((id) => !originalChapterIds.has(id));

    return nameChanged || chaptersChanged;
  };

  const canSave =
    name.trim().length > 0 &&
    selectedChapterIds.size > 0 &&
    !saving &&
    hasChanges() &&
    !loadingChapters;

  const handleSave = async () => {
    if (!canSave) return;

    if (containsProfanity(name.trim())) {
      setError('Profanity is not allowed.');
      return;
    }

    // case-insensitive duplicate check
    const isDuplicate = existingPlaylists.some(
      (p) =>
        normalizePlaylistName(p.name) === normalizePlaylistName(name) &&
        (!isEditMode || p.id !== editingPlaylist?.id),
    );
    if (isDuplicate) {
      setError('You already have a playlist with this name.');
      return;
    }

    setSaving(true);
    setError('');

    try {
      if (isEditMode) {
        const originalChapterIds = new Set(
          editingPlaylist.chapters.map((ch) => ch.chapterId),
        );

        const nameChanged =
          normalizePlaylistName(name) !==
          normalizePlaylistName(editingPlaylist.name);
        const chaptersChanged =
          selectedChapterIds.size !== originalChapterIds.size ||
          [...selectedChapterIds].some((id) => !originalChapterIds.has(id));

        const payload = {};
        if (nameChanged) payload.name = name.trim();
        if (chaptersChanged) payload.chapters = Array.from(selectedChapterIds);

        await updateCustomPlaylist(
          editingPlaylist.id,
          payload,
          chaptersChanged ? pfeSktId : undefined,
        );
      } else {
        await createCustomPlaylist(
          name.trim(),
          Array.from(selectedChapterIds),
          pfeSktId,
        );
      }
      onSuccess();
    } catch (err) {
      const responseData = err.response?.data;
      let message = isEditMode
        ? 'Failed to update playlist'
        : 'Failed to create playlist';

      if (typeof responseData === 'string') {
        message = responseData;
      } else if (responseData?.message) {
        message = responseData.message;
      }

      setError(message);
      setSaving(false);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <ModalBase
      isOpen={isOpen}
      toggle={!saving ? onClose : undefined}
      header={isEditMode ? 'Edit Custom Playlist' : 'Create Custom Playlist'}
      className="create-playlist-modal"
      body={
        <>
          <div className="create-playlist-modal__name-group">
            <label
              htmlFor="playlist-name"
              className="create-playlist-modal__label"
            >
              Playlist Name
            </label>
            <input
              id="playlist-name"
              type="text"
              className="form-control create-playlist-modal__input"
              placeholder="Enter playlist name..."
              value={name}
              onChange={handleNameChange}
              maxLength={MAX_NAME_LENGTH}
              disabled={saving}
            />
            <div className="create-playlist-modal__char-count">
              {name.length}/{MAX_NAME_LENGTH}
            </div>
            {error && (
              <div className="create-playlist-modal__error">{error}</div>
            )}
          </div>

          <ChapterSelector
            references={references}
            selectedChapterIds={selectedChapterIds}
            onSelectionChange={setSelectedChapterIds}
            loading={loadingChapters}
          />
        </>
      }
      footer={
        <>
          <button
            type="button"
            className="btn create-playlist-modal__btn create-playlist-modal__btn--cancel"
            onClick={handleCancel}
            disabled={saving}
          >
            CANCEL
          </button>
          <button
            type="button"
            className="btn create-playlist-modal__btn create-playlist-modal__btn--save"
            onClick={handleSave}
            disabled={!canSave}
          >
            {saving ? 'SAVING...' : 'SAVE'}
          </button>
        </>
      }
    />
  );
}

export default CreatePlaylistModal;
